import { motion } from "framer-motion";
import Reveal from "@/components/7fcc/Reveal";
import SectionHeader from "@/components/7fcc/SectionHeader";
import CTAButton from "@/components/7fcc/CTAButton";
import GhlCalendar from "@/components/7fcc/GhlCalendar";
import VideoTestimonials from "@/components/7fcc/VideoTestimonials";
import { APPLY } from "@/data/applyContent";
import { PROOF } from "@/data/content";
import { EASE } from "@/lib/motion";

export function ApplyHero() {
  const h = APPLY.hero;
  return (
    <section
      className="relative flex flex-col items-center overflow-hidden bg-ink-950 px-6 pb-16 pt-14 text-center md:pb-20 md:pt-16"
      data-testid="apply-hero"
    >
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-[2%] h-[42vh] w-[70vw] max-w-[880px] -translate-x-1/2 bg-[radial-gradient(ellipse,rgba(61,126,255,0.1),transparent_65%)] blur-3xl"
      />
      <div className="relative flex max-w-3xl flex-col items-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-light"
          data-testid="apply-eyebrow"
        >
          <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
          {h.eyebrow}
          <span className="h-px w-8 bg-brand/60" aria-hidden="true" />
        </motion.p>

        <h1 className="font-display text-[clamp(1.9rem,4vw,3rem)] font-semibold uppercase leading-[1.08] tracking-[-0.01em] text-fg">
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              data-testid="apply-headline"
            >
              {h.headlineStart}{" "}
              <span className="text-chrome">{h.headlineAccent}</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-fg-muted md:text-lg"
          data-testid="apply-sub"
        >
          {h.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
          className="mt-8"
        >
          <CTAButton label={h.ctaLabel} targetId={h.ctaTarget} testId="apply-hero-cta" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-9 max-w-xl border-t border-line pt-6 text-sm leading-relaxed text-fg-muted md:text-base"
          data-testid="apply-trust"
        >
          {h.trust}
        </motion.p>
      </div>
    </section>
  );
}

export function SeriousApplicants() {
  const s = APPLY.serious;
  return (
    <section id="serious-applicants" className="scroll-mt-20 bg-ink-900 px-6 py-16 md:py-24" data-testid="apply-serious">
      <div className="mx-auto max-w-5xl">
        <SectionHeader eyebrow={s.eyebrow} title={s.headline} support={s.lede} testId="apply-serious-heading" />

        <div className="mt-10 grid gap-5 md:grid-cols-3 md:mt-12">
          {s.cards.map((card, i) => (
            <Reveal key={card.num} delay={i * 0.08} className="h-full">
              <div className="h-full rounded-lg border border-line bg-ink-950/60 p-6" data-testid={`apply-card-${card.num}`}>
                <span className="font-display text-xs font-bold tracking-[0.24em] text-brand-light" aria-hidden="true">
                  {card.num}
                </span>
                <h3 className="mt-4 font-display text-lg font-medium text-fg">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fg-muted">{card.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            className="mt-10 rounded-lg border border-brand/30 bg-brand/[0.06] p-8 text-center md:mt-12 md:p-10"
            data-testid="apply-serious-panel"
          >
            <h3 className="font-display text-xl font-semibold text-fg md:text-2xl">{s.panel.title}</h3>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-fg-muted md:text-base">{s.panel.body}</p>
            <div className="mt-8">
              <CTAButton label={s.panel.ctaLabel} targetId={s.panel.ctaTarget} variant="chrome" testId="apply-panel-cta" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function OnTheCall() {
  const o = APPLY.onTheCall;
  return (
    <section className="bg-ink-950 px-6 py-16 md:py-24" data-testid="apply-on-the-call">
      <div className="mx-auto max-w-5xl">
        <SectionHeader eyebrow="On The Call" title={o.headline} support={o.lede} testId="apply-call-heading" />

        <div className="mt-10 grid gap-5 md:grid-cols-2 md:mt-12 lg:grid-cols-4">
          {o.steps.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08} className="h-full">
              <div
                className="flex h-full flex-col items-start rounded-lg border border-line bg-ink-900/60 p-6"
                data-testid={`apply-step-${i + 1}`}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-white"
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <h3 className="mt-4 font-display text-base font-medium text-fg">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ApplyProof() {
  // Reuses the exact /ready proof images, alt text and frame styling —
  // no duplicated data, same single source (PROOF in content.js).
  const shots = [
    PROOF.featured,
    PROOF.items.find((item) => item.src === "/proof/proof-07.png"),
    PROOF.items.find((item) => item.src === "/proof/proof-04.png"),
    PROOF.items.find((item) => item.src === "/proof/proof-02.png"),
  ];
  return (
    <section className="bg-ink-900 px-6 py-16 md:py-24" data-testid="apply-proof">
      <div className="mx-auto max-w-5xl">
        <SectionHeader eyebrow="Real Students" title="They Took The Call Too" testId="apply-proof-heading" />

        <div className="mt-10 md:mt-12">
          <VideoTestimonials testIdPrefix="apply-video-testimonial" />
        </div>

        <div
          className="mt-10 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4"
          data-testid="apply-proof-grid"
        >
          {shots.map((shot, i) => (
            <Reveal key={shot.src} delay={i * 0.06}>
              <figure
                className="overflow-hidden rounded-lg border border-line bg-ink-950"
                data-testid={`apply-proof-${i + 1}`}
              >
                <img src={shot.src} alt={shot.alt} loading="lazy" className="w-full" />
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 text-center">
            <CTAButton label="Book Your Call" targetId="calendar" testId="apply-proof-cta" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function BookingSection() {
  const b = APPLY.booking;
  return (
    <section id="booking" className="scroll-mt-20 bg-ink-900 px-6 py-16 text-center md:py-24" data-testid="apply-booking">
      <div className="mx-auto flex max-w-3xl flex-col items-center">
        <SectionHeader eyebrow={b.eyebrow} title={b.headline} support={b.sub} testId="apply-booking-heading" />
        <Reveal delay={0.2}>
          <div className="mt-9">
            <CTAButton label={b.ctaLabel} targetId={b.ctaTarget} testId="apply-booking-cta" />
          </div>
          <p className="mt-5 text-xs uppercase tracking-[0.18em] text-fg-dim" data-testid="apply-booking-note">
            {b.note}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function CalendarSection() {
  const c = APPLY.calendar;
  return (
    <section id="calendar" className="scroll-mt-20 bg-ink-950 px-6 py-16 md:py-24" data-testid="apply-calendar-section">
      <div className="mx-auto max-w-4xl">
        <SectionHeader eyebrow={c.eyebrow} title={c.headline} support={c.lede} testId="apply-calendar-heading" />
        <Reveal delay={0.1}>
          <div className="mt-10 rounded-lg border border-line bg-ink-900/60 p-3 md:p-4" data-testid="apply-calendar-frame">
            <GhlCalendar
              src={c.embed.src}
              scriptSrc={c.embed.scriptSrc}
              iframeId={c.embed.iframeId}
              title={c.embed.title}
              testId="apply-calendar-iframe"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
